import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import SEO from './SEO';
import {
  mainBranch,
  keralaServiceAreas,
  generateCitySpecificSchema,
  generateCityPageKeywords,
  generateLocationMetaDescription,
  getServiceAreaForCity,
  generateServiceAreaSchema
} from '../utils/seoHelpers';

const CityPageWrapper = styled.div`
  min-height: 100vh;
  padding: 80px 20px 40px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const CityHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h1 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 20px;
    font-weight: 700;

    .city-name {
      color: #ff6720;
    }
  }

  .subtitle {
    font-size: 1.2rem;
    color: #666;
    max-width: 700px;
    margin: 0 auto 30px;
    line-height: 1.6;
  }

  .service-badge {
    display: inline-block;
    background: #ff6720;
    color: white;
    padding: 8px 20px;
    border-radius: 25px;
    font-weight: 600;
    font-size: 0.9rem;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
`;

const ServiceCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  .icon {
    font-size: 3rem;
    margin-bottom: 20px;
    display: block;
  }

  h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.3rem;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
`;

const DeliveryInfo = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  margin-bottom: 60px;

  h2 {
    color: #ff6720;
    font-size: 2rem;
    margin-bottom: 30px;
    text-align: center;
  }

  .delivery-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }

  .delivery-details {
    .detail-item {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      font-size: 1.1rem;

      .icon {
        margin-right: 15px;
        color: #ff6720;
        font-size: 1.5rem;
      }

      .highlight {
        color: #ff6720;
        font-weight: 600;
      }
    }
  }

  .contact-info {
    background: #f8f9fa;
    padding: 30px;
    border-radius: 8px;
    text-align: center;

    h3 {
      color: #333;
      margin-bottom: 20px;
    }

    .contact-button {
      background: #ff6720;
      color: white;
      padding: 12px 30px;
      border-radius: 25px;
      font-weight: 600;
      text-decoration: none;
      display: inline-block;
      margin: 5px 10px;
      transition: all 0.3s ease;

      &:hover {
        background: #e55a1c;
        transform: translateY(-2px);
      }
    }
  }
`;

const ProductsSection = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  margin-bottom: 60px;

  h2 {
    color: #ff6720;
    margin-bottom: 30px;
    font-size: 2rem;
    text-align: center;
  }

  .intro-text {
    text-align: center;
    color: #666;
    font-size: 1.1rem;
    margin-bottom: 40px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
`;

const ProductCard = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 25px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff6720;
    transform: translateY(-3px);
  }

  .product-name {
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
    font-size: 1.1rem;
  }

  .product-brand {
    color: #ff6720;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 15px;
  }

  .availability {
    background: #e8f5e8;
    color: #2d5a2d;
    padding: 5px 15px;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #ff6720 0%, #e55a1c 100%);
  color: white;
  padding: 50px 40px;
  border-radius: 12px;
  text-align: center;

  h2 {
    margin-bottom: 20px;
    font-size: 2.2rem;
  }

  .subtitle {
    margin-bottom: 40px;
    font-size: 1.2rem;
    opacity: 0.9;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }

  .cta-buttons {
    display: flex;
    gap: 20px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .cta-button {
    background: white;
    color: #ff6720;
    padding: 15px 35px;
    border-radius: 30px;
    font-weight: 600;
    text-decoration: none;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);

    &:hover {
      background: #f8f9fa;
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    }
  }
`;

const BreadcrumbNav = styled.nav`
  margin-bottom: 30px;

  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    color: #666;

    a {
      color: #ff6720;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    span {
      color: #ccc;
    }
  }
`;

const CityPage = () => {
  const { city } = useParams();
  const [serviceArea, setServiceArea] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cityName = city?.charAt(0).toUpperCase() + city?.slice(1);
    const area = getServiceAreaForCity(cityName);
    setServiceArea(area);
    setLoading(false);
  }, [city]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const cityName = city?.charAt(0).toUpperCase() + city?.slice(1);
  const distance = serviceArea ? serviceArea.distance : "Available across Kerala";

  const featuredProducts = [
    { name: 'STIHL MS 170 Chainsaw', brand: 'STIHL', category: 'Chainsaw' },
    { name: 'STIHL BG 50 Blower', brand: 'STIHL', category: 'Blower' },
    { name: 'STIHL FS 55 Brushcutter', brand: 'STIHL', category: 'Brushcutter' },
    { name: 'STIHL HL 92 Hedge Trimmer', brand: 'STIHL', category: 'Hedge Trimmer' },
    { name: 'STIHL RE 90 Pressure Washer', brand: 'STIHL', category: 'Pressure Washer' },
    { name: 'STIHL RMA 235 Lawn Mower', brand: 'STIHL', category: 'Lawn Mower' }
  ];

  const structuredData = [
    generateCitySpecificSchema(cityName, featuredProducts),
    generateServiceAreaSchema(),
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://xplod-groups.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Service Areas",
          "item": "https://xplod-groups.com/locations"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": cityName,
          "item": `https://xplod-groups.com/locations/${city}`
        }
      ]
    }
  ];

  return (
    <CityPageWrapper>
      <SEO
        title={`Power Tools ${cityName} - STIHL Dealer with Home Delivery | XPLOD-GROUPS`}
        description={generateLocationMetaDescription(cityName)}
        keywords={generateCityPageKeywords(cityName)}
        canonical={`/locations/${city}`}
        type="webpage"
        structuredData={structuredData}
      />

      <Container>
        <BreadcrumbNav>
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/locations">Service Areas</Link>
            <span>›</span>
            <span>{cityName}</span>
          </div>
        </BreadcrumbNav>

        <CityHeader>
          <h1>Professional Power Tools in <span className="city-name">{cityName}</span></h1>
          <p className="subtitle">
            Authorized STIHL dealer serving {cityName} and surrounding areas with
            professional power tools, expert service, and reliable home delivery.
          </p>
          <div className="service-badge">
            🚚 Delivery Available: {distance}
          </div>
        </CityHeader>

        <ServiceGrid>
          <ServiceCard>
            <span className="icon">🛒</span>
            <h3>Wide Product Range</h3>
            <p>Complete selection of STIHL chainsaws, blowers, brushcutters, and garden equipment available for {cityName}</p>
          </ServiceCard>

          <ServiceCard>
            <span className="icon">🚚</span>
            <h3>Home Delivery</h3>
            <p>Fast and reliable delivery service to {cityName}. Free delivery on orders above ₹5,000</p>
          </ServiceCard>

          <ServiceCard>
            <span className="icon">🔧</span>
            <h3>Expert Service</h3>
            <p>Professional installation, repair, and maintenance services available for {cityName} customers</p>
          </ServiceCard>

          <ServiceCard>
            <span className="icon">📞</span>
            <h3>Local Support</h3>
            <p>Dedicated customer support and technical assistance for all {cityName} area customers</p>
          </ServiceCard>
        </ServiceGrid>

        <DeliveryInfo>
          <h2>🚚 Delivery to {cityName}</h2>
          <div className="delivery-grid">
            <div className="delivery-details">
              <div className="detail-item">
                <span className="icon">📍</span>
                <span>Delivery Distance: <span className="highlight">{distance}</span></span>
              </div>
              <div className="detail-item">
                <span className="icon">💰</span>
                <span>Free Delivery: <span className="highlight">Orders above ₹5,000</span></span>
              </div>
              <div className="detail-item">
                <span className="icon">⏱️</span>
                <span>Delivery Time: <span className="highlight">1-2 Working Days</span></span>
              </div>
              <div className="detail-item">
                <span className="icon">🛠️</span>
                <span>Installation: <span className="highlight">Professional Setup Available</span></span>
              </div>
            </div>

            <div className="contact-info">
              <h3>Order from {cityName}</h3>
              <p>Contact us for immediate assistance</p>
              <a href={`tel:${mainBranch.phone}`} className="contact-button">
                📞 Call Now
              </a>
              <a href="https://wa.me/919446189273" className="contact-button">
                💬 WhatsApp
              </a>
            </div>
          </div>
        </DeliveryInfo>

        <ProductsSection>
          <h2>🛠️ Popular Products in {cityName}</h2>
          <p className="intro-text">
            These professional-grade tools are popular among {cityName} customers and available for immediate delivery
          </p>

          <ProductGrid>
            {featuredProducts.map((product, index) => (
              <ProductCard key={index}>
                <div className="product-name">{product.name}</div>
                <div className="product-brand">{product.brand}</div>
                <div className="availability">✅ Available in {cityName}</div>
              </ProductCard>
            ))}
          </ProductGrid>

          <div style={{ textAlign: 'center' }}>
            <Link
              to="/collections/chainsaws"
              style={{
                background: '#ff6720',
                color: 'white',
                padding: '12px 30px',
                borderRadius: '25px',
                textDecoration: 'none',
                fontWeight: '600',
                marginRight: '15px'
              }}
            >
              🔗 View All Chainsaws
            </Link>
            <Link
              to="/collections/blowers"
              style={{
                background: '#ff6720',
                color: 'white',
                padding: '12px 30px',
                borderRadius: '25px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              💨 View All Blowers
            </Link>
          </div>
        </ProductsSection>

        <CTASection>
          <h2>Ready to Order in {cityName}?</h2>
          <p className="subtitle">
            Get expert advice, competitive prices, and reliable delivery for all your power tool needs in {cityName}
          </p>
          <div className="cta-buttons">
            <a href={`tel:${mainBranch.phone}`} className="cta-button">
              📞 Call for Quote
            </a>
            <a href="/contact" className="cta-button">
              💬 Send Inquiry
            </a>
            <a href="/collections/chainsaws" className="cta-button">
              🛍️ Browse Products
            </a>
          </div>
        </CTASection>
      </Container>
    </CityPageWrapper>
  );
};

export default CityPage;
